<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div v-if="location" class="mb-4">
          <a :href="`/businesses/${location.business_id}`" class="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            &larr; Back to {{ location.business_name }}
          </a>
        </div>
        <div v-if="location" class="flex items-start justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {{ location.name }}
            </h1>
            <p v-if="location.address" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ location.address }}
            </p>
          </div>
          <button
            @click="openEditForm"
            class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
            Edit
          </button>
        </div>
      </div>
    </div>

    <div v-if="location" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav class="flex gap-6 -mb-px">
          <button
            @click="activeTab = 'details'"
            :class="activeTab === 'details' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Details
          </button>
          <button
            @click="activeTab = 'analytics'"
            :class="activeTab === 'analytics' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Analytics
          </button>
          <button
            @click="activeTab = 'reviews'"
            :class="activeTab === 'reviews' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Reviews
          </button>
          <button
            @click="activeTab = 'gbp_posts'"
            :class="activeTab === 'gbp_posts' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            GBP Posts
          </button>
          <button
            @click="activeTab = 'negative_keywords'"
            :class="activeTab === 'negative_keywords' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'"
            class="py-2 px-1 border-b-2 font-medium text-sm">
            Negative Keywords
          </button>
        </nav>
      </div>

      <AnalyticsTab
        v-if="activeTab === 'analytics'"
        :location-id="locationId" />

      <ReviewsTab
        v-if="activeTab === 'reviews'"
        :location-id="locationId" />

      <GbpCampaigns
        v-if="activeTab === 'gbp_posts'"
        :location-id="locationId"
        :default-keywords="location.target_keywords || []" />

      <LocationNegativeKeywordsTab
        v-if="activeTab === 'negative_keywords' && location"
        :location-id="locationId"
        :business-id="location.business_id"
        :account-id="accountId"
        :csrf-token="csrfToken"
        :available-services="availableServices"
        :default-location="defaultLocation" />

      <div v-if="activeTab === 'details'" :class="hasRightColumn ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' : 'max-w-2xl'">
        <div class="space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Contact Information
            </h3>
            <dl class="space-y-3">
              <div v-if="location.phone_number">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Phone
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.phone_number }}
                </dd>
              </div>
              <div v-if="location.email">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Email
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.email }}
                </dd>
              </div>
              <div v-if="location.website_url">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Website
                </dt>
                <dd class="text-sm">
                  <a :href="location.website_url" target="_blank" class="text-primary-600 dark:text-primary-400 hover:underline">{{ location.website_url }}</a>
                </dd>
              </div>
              <div v-if="location.place_id">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Place ID
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono text-xs">
                  {{ location.place_id }}
                </dd>
              </div>
            </dl>
          </div>

          <div v-if="location.address_line_1 || location.city" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Address Details
            </h3>
            <dl class="space-y-3">
              <div v-if="location.address_line_1">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Address Line 1
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.address_line_1 }}
                </dd>
              </div>
              <div v-if="location.address_line_2">
                <dt class="text-xs text-gray-500 dark:text-gray-400">
                  Address Line 2
                </dt>
                <dd class="text-sm text-gray-900 dark:text-gray-100">
                  {{ location.address_line_2 }}
                </dd>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div v-if="location.city">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    City
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.city }}
                  </dd>
                </div>
                <div v-if="location.state">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    State
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.state }}
                  </dd>
                </div>
                <div v-if="location.zip">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    ZIP
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.zip }}
                  </dd>
                </div>
                <div v-if="location.country">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Country
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100">
                    {{ location.country }}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="space-y-6">
          <div v-if="location.services && location.services.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Services
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="service in location.services"
                :key="service.name"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                {{ service.name }}
              </span>
            </div>
          </div>

          <div v-if="location.target_keywords && location.target_keywords.length > 0" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Target Keywords
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="keyword in location.target_keywords"
                :key="keyword"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                {{ keyword }}
              </span>
            </div>
          </div>

          <div v-if="hasCoordinates" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Coordinates
            </h3>
            <dl class="space-y-2">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Latitude
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono">
                    {{ location.lat || location.cid_lat || location.place_id_lat }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500 dark:text-gray-400">
                    Longitude
                  </dt>
                  <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono">
                    {{ location.lng || location.cid_lng || location.place_id_lng }}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getLocation } from '~/javascript/api/locations.js'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import LocationForm from './form.vue'
import GbpCampaigns from '~/javascript/components/gbp_campaigns/index.vue'
import AnalyticsTab from './analytics_tab.vue'
import ReviewsTab from './reviews_tab.vue'
import LocationNegativeKeywordsTab from '~/javascript/components/negative_keywords/location_negative_keywords_tab.vue'

const props = defineProps({
  locationId: { type: String, required: true },
  accountId: { type: String, default: '' },
  csrfToken: { type: String, default: '' },
  availableServices: { type: Array, default: () => []},
  defaultLocation: { type: String, default: '' }
})

const location = ref(null)
const slidePanel = ref(null)
const activeTab = ref('details')

const hasCoordinates = computed(() => {
  if (!location.value) return false
  return location.value.lat || location.value.cid_lat || location.value.place_id_lat
})

const hasRightColumn = computed(() => {
  if (!location.value) return false
  return (location.value.services && location.value.services.length > 0) ||
    (location.value.target_keywords && location.value.target_keywords.length > 0) ||
    hasCoordinates.value
})

const fetchLocation = async() => {
  try {
    const response = await getLocation(props.locationId)
    location.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching location:', error)
  }
}

const onLocationSaved = () => {
  slidePanel.value.close()
  fetchLocation()
}

const openEditForm = () => {
  slidePanel.value.open({
    component: LocationForm,
    props: { location: location.value, businessId: location.value.business_id },
    events: { saved: onLocationSaved }
  })
}

onMounted(() => {
  hideLoadingSpinner()
  fetchLocation()
})
</script>
